import { ExperimentValue, type UseExperimentReturn, type UseExperimentReturnReadonly, type ExperimentOptions, type ExperimentNameUnion } from '../types'
import { useCookie, useState, useDayjs, useExperimentVersions, useCoreConfig, useCoreLogger, readonly, computed } from '#imports'

/**
 * Composable for reading or writing A/B experiment group assignments.
 *
 * **Read-only mode** (default, `allowWrite` omitted or `false`):
 * Returns a readonly computed of the current value. On SSR it reads from the
 * shared `useState` (so the middleware's write-mode assignment is visible
 * within the same request); on the client it reads from the cookie (so
 * cleanup, manual DevTools edits, and post-signup middleware writes flow
 * through reactively). Defaults to `'a'` when neither is set. This is the
 * mode components should use.
 *
 * **Write mode** (`allowWrite: true`):
 * If the user already has an assignment in state, returns it.
 * Otherwise selects a group and persists it to both state and a cookie (30-day TTL).
 * Only the experiment middleware should use this mode.
 *
 * **Selection logic (write mode):**
 * 1. If experiment is not in `nuxt.config.ts` → returns `'a'` (logs error in dev)
 * 2. If only one version configured → returns that version (logs error in dev)
 * 3. If a valid cookie exists → returns cookie value
 * 4. Otherwise → randomly selects from configured version keys
 *
 * Note: `userId`-based deterministic assignment is handled in the middleware
 * before calling this composable by pre-setting the cookie.
 *
 * **Cookie config:** `sameSite: 'lax'`, `maxAge: 30 days`, `path: '/'`,
 * `secure: true`, `httpOnly: false`
 *
 * @param options - Experiment identification and write permission
 * @returns Ref containing the assigned group (`'a'`, `'b'`, etc.)
 *
 * @example
 * // Reading in a component (readonly)
 * const experiment = useExperiment({
 *     cookieName: ExperimentCookieName.LANDING_PAGE,
 *     experimentName: ExperimentName.LANDING_PAGE,
 * })
 * // experiment.value === 'a' | 'b'
 *
 * @example
 * // Writing in middleware
 * useExperiment({
 *     cookieName: experiment.cookieName,
 *     experimentName: experiment.name,
 *     allowWrite: true,
 * })
 */
export function useExperiment(options: ExperimentOptions & { allowWrite: true, experimentName: ExperimentNameUnion }): UseExperimentReturn
export function useExperiment(options: ExperimentOptions & { allowWrite?: false }): UseExperimentReturnReadonly
export function useExperiment(options: ExperimentOptions): unknown {
    const { cookieName, experimentName, allowWrite } = options
    const { env } = useCoreConfig()

    const logger = useCoreLogger().withTag('useExperiment')
    const state = useState<ExperimentValue>(cookieName)
    const versions = useExperimentVersions(experimentName)

    const dayjs = useDayjs()
    const cookie = useCookie<ExperimentValue>(cookieName, {
        sameSite: 'lax',
        maxAge: dayjs.duration({ days: 30 }).asSeconds(), // 30 days
        path: '/',
        secure: true,
        httpOnly: false,
    })

    // Read-only consumers derive from state on SSR and from the cookie on the
    // client:
    //   - SSR: each `useCookie` call creates a fresh ref that reads request
    //     headers, so the middleware's write-mode cookie.value is invisible
    //     to a separate readonly call within the same request. `useState`
    //     IS shared by key across calls, so it reliably reflects the
    //     middleware's write within the render.
    //   - Client: the cookie is the live source of truth — it picks up
    //     middleware writes (via Set-Cookie → document.cookie), the
    //     cleanup middleware clearing retired cookies, and manual DevTools
    //     edits, all without re-entering write mode.
    // `state` is never written from the readonly path, so the original
    // poisoning bug (state stuck on 'a' from a pre-auth read) stays fixed.
    if (!allowWrite) {
        return readonly(computed(() => {
            if (import.meta.server) return state.value ?? ExperimentValue.A
            return cookie.value ?? ExperimentValue.A
        }))
    }

    if (state.value) return state

    function selectExperiment(experimentName: ExperimentNameUnion): ExperimentValue {
        if (!versions) {
            if (env === 'dev') {
                logger.error(`Missing experiment: "${experimentName}" from the "core" module in "nuxt.config.ts"`)
            }

            return ExperimentValue.A
        }

        const versionKeys = Object.keys(versions || {}) as ExperimentValue[]

        if (!versionKeys?.length) {
            if (env === 'dev') {
                logger.error(`Missing experiment: "${experimentName}" from the "core" module in "nuxt.config.ts"`)
            }

            return ExperimentValue.A
        }

        if (versionKeys.length === 1) {
            const firstVersionKey = versionKeys[0] as ExperimentValue

            if (env === 'dev') {
                logger.error(`Experiment has only set one option for the experiment: "${experimentName}" in the "core" module in "nuxt.config.ts". The experiment value will be set to: "${firstVersionKey}"`)
            }

            return firstVersionKey
        }

        if (cookie.value && versionKeys.includes(cookie.value)) {
            return cookie.value
        }

        const randomNumber = Math.floor(Math.random() * versionKeys.length)

        return versionKeys?.[randomNumber] as ExperimentValue
    }

    if (versions) {
        cookie.value = state.value = selectExperiment(experimentName)
    }
    else {
        state.value = selectExperiment(experimentName)
    }

    return state
}
